<template>
  <ui-readonly-field v-if="isFormReadonly" :label="$attrs.label" :value="readonlyValue" />
  <v-popover
    v-else-if="typeof saving === 'boolean'"
    placement="right" 
    trigger="manual"
    offset="8"
    popover-class="input-saving--popover arrow--hidden"
    :delay="{hide: 1500}"
    :open="saving"
    :auto-hide="false"
  >
    <v-text-field
      ref="ui-input"
      v-model="getValue"
      :error-messages="getErrorMsg"
      :error="error"
      outlined
      shaped
      :hide-details="hideDetails"
      v-bind="$attrs"
      v-on="$listeners"
      :background-color="backgroundColor"
      class="ui-text-field"
      :class="{
        'background-custom': backgroundColor,
        'custom-value': !isDefaultValue,
        'percent-icon': percentIcon,
      }"
      @keydown="$attrs.type === 'number' ? inputValidate($event) : undefined"
      @input="validateRange"
      @blur="!valueSaved ? onBlurEmit() : validateRange()"
    >
      <template v-for="(_, name) in $slots" v-slot:[name]>
        <slot :name="name" />
      </template>
    </v-text-field>
    <template #popover>
      <div class="tooltip--data">
        <v-progress-circular
          v-if="saving"
          color="blue500"
          indeterminate
          :size="10"
          :width="2"
          class="d-flex mr-2"
        />
        <v-icon size="10" v-else>mdi-check-circle</v-icon>
        {{ $t(`base.${saving ? 'saving' : 'saved'}`) }}
      </div>
    </template>
  </v-popover>
  <v-text-field
    v-else
    ref="ui-input"
    v-model="getValue"
    :error-messages="getErrorMsg"
    :error="error"
    outlined
    shaped
    :hide-details="hideDetails"
    v-bind="$attrs"
    v-on="$listeners"
    :background-color="backgroundColor"
    class="ui-text-field"
    :class="{
      'background-custom': backgroundColor,
      'custom-value': !isDefaultValue,
      'percent-icon': percentIcon,
    }"
    @keydown="$attrs.type === 'number' ? inputValidate($event) : undefined"
    @input="validateRange"
    @blur="!valueSaved ? onBlurEmit() : validateRange()"
  >
    <template v-for="(_, name) in $slots" v-slot:[name]>
      <slot :name="name" />
    </template>
  </v-text-field>
</template>

<script>
import { getErrorMsg } from '@/helpers/validation'
import IMask from 'imask'
import debounce from 'lodash/debounce'
import { INPUT_DELAY_TIME } from '@/constants/variables'
import FormReadonlyMixin from '@/mixins/FormReadonlyMixin'

export default {
  name: 'ui-text-field',
  mixins: [FormReadonlyMixin],
  props: {
    value: null,
    errorMessages: {
      type: [String, Array, Object],
    },
    hideDetails: {
      type: [Boolean, String],
      default: 'auto',
    },
    isDefaultValue: {
      type: Boolean,
      default: true,
    },
    saving: {
      type: Boolean,
      default: null,
    },
    percentIcon: {
      type: Boolean,
      default: false,
    },
    integer: {
      type: Boolean,
      default: false,
    },
    positive: {
      type: Boolean,
      default: false,
    },
    mask: {
      type: String,
      default: '',
    },
    returnZero: {
      type: Boolean,
      default: false,
    },
    backgroundColor: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      maskValue: null,
      currentValue: null,
      valueSaved: true,
    }
  },
  computed: {
    getValue: {
      get() {
        if (this.mask && this.maskValue) {
          this.maskValue.updateValue()
          return this.maskValue?.value
        }
        return this.value
      },
      set(v) {
        if(typeof this.saving === 'boolean') this.valueSaved = false
        if (this.mask) {
          if(this.maskValue) this.maskValue.updateValue()
          this.currentValue = v.replace(/\s/g, '')
        } else this.currentValue = v
        this.$emit('input', this.currentValue)
        this.inputDebounce()
      },
    },
    rawErrors() {
      if (Array.isArray(this.errorMessages) || typeof this.errorMessages === 'string') {
        return this.errorMessages
      }
      return getErrorMsg(this.errorMessages)
    },
    error() {
      if (Array.isArray(this.rawErrors) && this.rawErrors.length && this.rawErrors.every(e => e === '\u200B')) return true
      if (!this.value) return this.$attrs.error
      return this.value.$error
    },
    readonlyValue() {
      if (this.value === '' || this.value == null) return this.value
      const { prefix, suffix } = this.$attrs
      return [prefix, this.value, suffix].filter(v => v !== undefined && v !== '').join(' ')
    },
    getErrorMsg() {
      if (Array.isArray(this.rawErrors)) return this.rawErrors.filter(e => e !== '\u200B')
      if (typeof this.rawErrors === 'string') return this.rawErrors === '\u200B' ? [] : this.rawErrors
      return this.rawErrors
    },
  },
  mounted() {
    if (this.mask && this.$refs['ui-input']) {
      const maskOptions = {
        mask: Number,
        scale: 0,
        signed: false,
        thousandsSeparator: ' ',
        padFractionalZeros: true,
      }
      const input = this.$refs['ui-input'].$refs.input
      this.maskValue = IMask(input, maskOptions)
    }
  },
  methods: {
    onBlurEmit(){
      this.$emit('inputDelay', this.currentValue)
      this.valueSaved = true
    },
    inputValidate(event){
      if (event.key === 'e' || event.key === 'E') return event.preventDefault();
      if (this.integer && (event.key === '.' || event.key === ',')) return event.preventDefault();
      if (this.positive && event.key === '-') return event.preventDefault();
    },
    validateRange() {
      if (this.$attrs.type !== 'number' || !this.returnZero) return
      let val = this.currentValue || this.getValue
      if (typeof val === 'string') {
        val = val.replace(/\s/g, '')
      }
      val = Number(val)
      if (isNaN(val)) return
      const { min, max } = this.$attrs
      if (min !== undefined && val < Number(min)) {
        this.currentValue = String(min)
      }
      if (max !== undefined && val > Number(max)) {
        this.currentValue = String(max)
      }
      if(!this.currentValue) this.currentValue = val
      this.$emit('input', this.currentValue)
      this.inputDebounce()
    },
    inputDebounce: debounce(function(){
      if (this.valueSaved) return
      this.$emit('inputDelay', this.currentValue)
      this.valueSaved = true
    }, INPUT_DELAY_TIME),
  }
}
</script>

<style lang="scss">
.ui-text-field {
  .v-label--active {
    color: var(--v-black-darken3) !important;
  }

  fieldset,
  .v-text-field .v-input__control {
    border-color: var(--v-black-lighten6);
  }

  .v-text-field__slot {
    .v-label {
      font-size: 14px !important;
      color: var(--v-black-darken1);
    }
    .v-text-field__prefix {
      font-size: 12px !important;
      color: var(--v-black-darken3);
    }
    input {
      font-size: 12px;
      color: var(--v-black-darken3) !important;

      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:focus {
        -webkit-text-fill-color: var(--v-black-darken3);
        -webkit-box-shadow: 0 0 0px 1000px #fff inset;
        transition: background-color 5000s ease-in-out 0s;
      }
    }

    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    input[type='number'] {
      -moz-appearance: textfield;
    }
  }

  .v-input__append-inner {
    margin-top: 12px !important;
  }

  &.background-custom {
    .v-text-field__slot input {
      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:focus {
        -webkit-text-fill-color: var(--v-black-darken3);
        -webkit-box-shadow: 0 0 0 1000px #ffffff00 inset;
        transition: background-color 5000s ease-in-out 0s;
      }
    }
  }

  &.custom-value .v-text-field__slot input {
    color: var(--v-blue500-base) !important;
  }
  &.percent-icon .v-input__slot {
    min-height: 36px!important;
    .v-text-field__slot input {
      padding-right: 18px;
      border-left: none;
      border-right: none;
      background: url(../../../../assets/images/partner-spend-persent.svg) no-repeat scroll 17px 7px;
      &--disabled {
        background: url(../../../../assets/images/partner-spend-persent.svg) no-repeat scroll 17px 7px, var(--v-gray50-base);
      }
    }
  }
}

.ui-text-field__append-outer-reset {
  .v-input__append-outer {
    margin-top: 0 !important;
  }
}

.tooltip.input-saving--popover {
  .wrapper .tooltip-inner {
    padding: 4px;
  }
}
</style>
